import type { RibbonId } from 'owa-ribbon-ids';
import { isUserCreatedRibbonGroupId } from 'owa-ribbon-ids';
import type { MailRibbonGroup } from 'owa-mail-ribbon-store-shared-types';
import { defaultConfig } from 'owa-mail-ribbon-store-shared-types';

/**
 * We need to scan through the user's ribbon configuration and ensure that the RibbonIds
 * are found in defaultConfig. This scenario is possible because a user may log into OWA,
 * customize their Ribbon, and then log into a really old version of Monarch where those RibbonIds
 * don't exist in the product yet. In that case, Monarch would break.
 *
 * To remedy this, we scan through the user's ribbon configuration, and as long as the RibbonIds exist
 * within defaultConfig, we consider the configuration valid. However, if we find a RibbonId that does not
 * exist at all in defaultConfig, then we consider the config corrupted.
 *
 * @param ribbonConfig the ribbon config that the user currently has
 * @returns true if the configuration is valid, false if not.
 */
export function ribbonValidityCheck(ribbonConfig: any): boolean {
    // We only care about SLR and Toolbar as those are the only customizable Ribbons.
    if (!ribbonConfig.singleline || !ribbonConfig.toolbar) {
        return false;
    }

    // Push all ribbonIds from customizable Ribbon tabs into Set to allow for validation.
    const validDefaultConfigIds: Set<RibbonId> = getValidDefaultConfigIds();

    return (
        validateUserConfigLayout(validDefaultConfigIds, ribbonConfig.singleline.homeTab.layout) &&
        validateUserConfigLayout(validDefaultConfigIds, ribbonConfig.singleline.viewTab.layout) &&
        validateUserConfigLayout(
            validDefaultConfigIds,
            ribbonConfig.singleline.messageTab.layout
        ) &&
        validateUserConfigLayout(validDefaultConfigIds, ribbonConfig.toolbar.homeTab.layout) &&
        validateUserConfigLayout(validDefaultConfigIds, ribbonConfig.toolbar.messageTab.layout)
    );
}

/**
 * Constructs and returns a Set() of all used RibbonIds that exists in this session's defaultConfig.
 */
function getValidDefaultConfigIds(): Set<RibbonId> {
    let validDefaultConfigIds: Set<RibbonId> = new Set();

    addToValidDefaultConfigIds(validDefaultConfigIds, defaultConfig.singleline.homeTab.layout);
    addToValidDefaultConfigIds(validDefaultConfigIds, defaultConfig.singleline.viewTab.layout);
    addToValidDefaultConfigIds(validDefaultConfigIds, defaultConfig.singleline.messageTab.layout);
    addToValidDefaultConfigIds(validDefaultConfigIds, defaultConfig.toolbar.homeTab.layout);
    addToValidDefaultConfigIds(validDefaultConfigIds, defaultConfig.toolbar.messageTab.layout);

    return validDefaultConfigIds;
}

/**
 * Adds to validDefaultConfigIds the defaultConfigLayout's groupId and its controlIds[].
 * This builds up the set of valid RibbonIds.
 * @param validDefaultConfigIds Set() that will be altered by this function and appended to.
 * @param defaultConfigLayout A MailRibbonGroup[] from defaultConfig.
 */
function addToValidDefaultConfigIds(
    validDefaultConfigIds: Set<RibbonId>,
    defaultConfigLayout: MailRibbonGroup[]
) {
    defaultConfigLayout.forEach(group => {
        validDefaultConfigIds.add(group.groupId);
        group.controlIds.forEach(controlId => {
            validDefaultConfigIds.add(controlId);
        });
    });
}

/**
 * Go through the user's layout and ensure that all IDs are considered valid.
 * @param validDefaultConfigIds Set of valid RibbonIds from defaultConfig
 * @param userConfigLayout User's layout that we are testing
 * return true if all IDs in layout are valid, false if found any invalid ID
 */
function validateUserConfigLayout(
    validDefaultConfigIds: Set<RibbonId>,
    userConfigLayout: MailRibbonGroup[]
): boolean {
    for (let group of userConfigLayout) {
        if (!isGroupIdValid(validDefaultConfigIds, group.groupId)) {
            return false;
        }

        for (let controlId of group.controlIds) {
            if (!isControlIdValid(validDefaultConfigIds, controlId)) {
                return false;
            }
        }
    }
    return true;
}

/**
 * Checks if the groupId is valid, meaning within our current version of defaultConfig or a user-created GroupId.
 * @param validDefaultConfigIds Set of valid RibbonIds from defaultConfig
 * @param groupId The RibbonId we're testing to see if valid
 * @returns true if valid, false if found an invalid RibbonId
 */
function isGroupIdValid(validDefaultConfigIds: Set<RibbonId>, groupId: RibbonId): boolean {
    return isUserCreatedRibbonGroupId(groupId) || validDefaultConfigIds.has(groupId);
}

/**
 * Checks if the controlId is valid, meaning within our current version of defaultConfig.
 * @param validDefaultConfigIds Set of valid RibbonIds from defaultConfig
 * @param controlId The RibbonId we're testing to see if valid
 * @returns true if valid, false if found an invalid RibbonId
 */
function isControlIdValid(validDefaultConfigIds: Set<RibbonId>, controlId: RibbonId): boolean {
    return validDefaultConfigIds.has(controlId);
}
